import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container, Paper, Box, Typography, Checkbox, FormControlLabel,
  Divider, Button, CircularProgress, Snackbar, Alert,
  Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import BaseURL from "../../api/AppURL";

export default function EditRolePermissionAssign() {
  const { id } = useParams();
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const handleSnackbarClose = () => setSnackbar(s => ({ ...s, open: false }));

  useEffect(() => {
    fetchRoleData();
  }, [id]);

  const fetchRoleData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${BaseURL}/admin/edit/roles/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
      });

      const roleData = res.data.role;
      setSelectedRole(roleData);
      setPermissions(res.data.permissions || []);
      setGroups(res.data.permission_groups || []);
      setSelectedPermissions(roleData.role_permissions_id || []);

      // Accordion: ডিফল্ট expandedGroups তৈরি করা
      const initialExpanded = {};
      (res.data.permission_groups || []).forEach(group => {
        const groupPerms = (res.data.permissions || []).filter(p => p.group_name === group.group_name);
        const hasSelected = groupPerms.some(p => roleData.role_permissions_id.includes(p.id));
        initialExpanded[group.group_name] = hasSelected; // selected থাকলে expand
      });
      setExpandedGroups(initialExpanded);

    } catch (err) {
      setSnackbar({ open: true, severity: "error", message: "Failed to load role data" });
    } finally {
      setLoading(false);
    }
  };

  const handleGroupToggle = (groupName, checked) => {
    const groupPermissions = permissions.filter(p => p.group_name === groupName).map(p => p.id);
    if (checked) {
      setSelectedPermissions(prev => [...new Set([...prev, ...groupPermissions])]);
    } else {
      setSelectedPermissions(prev => prev.filter(id => !groupPermissions.includes(id)));
    }
  };

  const handlePermissionToggle = (permissionId, checked) => {
    if (checked) {
      setSelectedPermissions(prev => [...prev, permissionId]);
    } else {
      setSelectedPermissions(prev => prev.filter(id => id !== permissionId));
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.put(
        `${BaseURL}/admin/roles/update/${selectedRole.id}`,
        { permission: selectedPermissions },
        { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } }
      );

      setSnackbar({
        open: true,
        severity: "success",
        message: res.data.message || "Updated successfully"
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update permissions"
      });
    }
  };

  const handleAccordionChange = (groupName) => (event, isExpanded) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: isExpanded
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPermissions(permissions.map(p => p.id));
    } else {
      setSelectedPermissions([]);
    }
  };

  const allSelected = permissions.length > 0 && permissions.every(p => selectedPermissions.includes(p.id));

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Edit Permissions for Role
        </Typography>

        {/* Role Name Readonly */}
        <Box sx={{ mb: 2 }}>
          <input
            type="text"
            value={selectedRole?.name || ""}
            readOnly
            style={{
              width: "100%",
              padding: "8px",
              fontSize: "16px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: "#f5f5f5"
            }}
          />
        </Box>

        {/* Permission All Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
          }
          label="Permission All"
        />
        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {groups.map(group => {
              const groupPermissions = permissions.filter(p => p.group_name === group.group_name);
              const isGroupSelected = groupPermissions.length > 0 &&
                groupPermissions.every(p => selectedPermissions.includes(p.id));

              return (
                <Accordion
                  key={group.group_name}
                  expanded={!!expandedGroups[group.group_name]}
                  onChange={handleAccordionChange(group.group_name)}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: "#f5f5f5" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isGroupSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleGroupToggle(group.group_name, e.target.checked);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      }
                      label={group.group_name}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ pl: 2 }}>
                      {groupPermissions.map(perm => (
                        <FormControlLabel
                          key={perm.id}
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={e =>
                                handlePermissionToggle(perm.id, e.target.checked)
                              }
                            />
                          }
                          label={perm.name}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Button variant="contained" color="primary" onClick={handleSave} sx={{ mt: 2 }}>
              Save Changes
            </Button>
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}